import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { GovQuoteDashboard } from './GovQuoteDashboard'
import { GovRfqModal } from './GovRfqModal'
import { GovernmentNav } from '../../../components/navigation/GovernmentNav'
import { 
  FileText, 
  Users, 
  TrendingUp, 
  Shield, 
  Award,
  Plus,
  Settings,
  BarChart3
} from 'lucide-react'

interface GovDashboardProps {
  userRole: 'ENTERPRISE_VENDOR' | 'AGENCY' | 'ADMIN' | 'GOV_EMAIL'
}

interface DashboardStats {
  totalRfqs: number
  activeRfqs: number
  totalProposals: number
  recentProposals: number
  uniqueVendors: number
  complianceScore: number
  certifications: number
}

export const GovDashboard: React.FC<GovDashboardProps> = ({ userRole }) => {
  const [stats, setStats] = useState<DashboardStats>({
    totalRfqs: 0,
    activeRfqs: 0,
    totalProposals: 0,
    recentProposals: 0,
    uniqueVendors: 0,
    complianceScore: 98,
    certifications: 5
  })
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [isRfqModalOpen, setIsRfqModalOpen] = useState(false)

  useEffect(() => {
    fetchDashboardData()
  }, [userRole])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      // Use mock data for now since the API endpoint doesn't exist yet
      const mockData = {
        stats: {
          totalRfqs: 12,
          activeRfqs: 8,
          totalProposals: 45,
          recentProposals: 3,
          uniqueVendors: 23,
          complianceScore: 98,
          certifications: 5
        },
        quotes: []
      }
      
      setStats(mockData.stats)
      setQuotes(mockData.quotes)
    } catch (error) {
      console.error('Error fetching government dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleQuoteAction = (action: string, quoteId: string) => {
    console.log(`Government ${action} action for quote ${quoteId}`)
    // Handle government-specific quote actions
  }

  const handleRfqSuccess = () => {
    setIsRfqModalOpen(false)
    fetchDashboardData()
  }

  const statsCards = [
    {
      title: userRole === 'ENTERPRISE_VENDOR' ? 'RFQ Responses' : 'Active RFQs',
      value: stats.activeRfqs,
      icon: <FileText className="w-6 h-6" />,
      color: 'blue',
      change: '+12%'
    },
    {
      title: userRole === 'ENTERPRISE_VENDOR' ? 'Proposals Submitted' : 'Total Proposals',
      value: stats.totalProposals,
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'green',
      change: '+8%'
    },
    {
      title: 'Compliance Score',
      value: `${stats.complianceScore}%`,
      icon: <Shield className="w-6 h-6" />,
      color: 'purple',
      change: '+2%'
    },
    {
      title: 'Certifications',
      value: stats.certifications,
      icon: <Award className="w-6 h-6" />,
      color: 'orange',
      change: 'Active'
    }
  ]

  return (
    <div className="gov-dashboard min-h-screen bg-gray-50 dark:bg-gray-900">
      <GovernmentNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Government Contracting Dashboard
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {userRole === 'ENTERPRISE_VENDOR' 
                  ? 'Manage your government RFQ responses and proposals'
                  : userRole === 'ADMIN'
                  ? 'Administrative access to government contracting system'
                  : userRole === 'GOV_EMAIL'
                  ? 'Government contracting system access'
                  : 'Manage government RFQs and vendor proposals'
                }
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {userRole === 'AGENCY' && (
                <button
                  onClick={() => setIsRfqModalOpen(true)}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Create RFQ</span>
                </button>
              )}
              <button className="btn btn-outline flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>Settings</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsCards.map((stat, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {stat.value}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {stat.change}
                  </p>
                </div>
                <div className={`p-3 rounded-full bg-${stat.color}-100 dark:bg-${stat.color}-900/20`}>
                  <div className={`text-${stat.color}-600 dark:text-${stat.color}-400`}>
                    {stat.icon}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {userRole === 'ENTERPRISE_VENDOR' ? (
              <>
                <Link to="/gov/rfqs" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <FileText className="w-6 h-6 text-blue-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Browse RFQs</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Find government opportunities</p>
                  </div>
                </Link>
                <Link to="/gov/proposals" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <BarChart3 className="w-6 h-6 text-green-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">My Proposals</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Track proposal performance</p>
                  </div>
                </Link>
                <Link to="/gov/compliance" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <Shield className="w-6 h-6 text-purple-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Compliance</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Manage certifications</p>
                  </div>
                </Link>
              </>
            ) : userRole === 'ADMIN' ? (
              <>
                <Link to="/admin" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <Settings className="w-6 h-6 text-blue-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Admin Panel</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">System administration</p>
                  </div>
                </Link>
                <Link to="/gov/vendors" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <Users className="w-6 h-6 text-green-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Certified Vendors</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Browse vendor directory</p>
                  </div>
                </Link>
                <Link to="/gov/proposals" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <BarChart3 className="w-6 h-6 text-purple-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">All Proposals</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Review all proposals</p>
                  </div>
                </Link>
              </>
            ) : userRole === 'GOV_EMAIL' ? (
              <>
                <Link to="/gov/vendors" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <Users className="w-6 h-6 text-green-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Certified Vendors</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Browse vendor directory</p>
                  </div>
                </Link>
                <Link to="/gov/proposals" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <BarChart3 className="w-6 h-6 text-purple-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">All Proposals</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Review all proposals</p>
                  </div>
                </Link>
                <Link to="/admin" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <Settings className="w-6 h-6 text-blue-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Admin Panel</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">System administration</p>
                  </div>
                </Link>
              </>
            ) : (
              <>
                <Link to="/gov/rfqs/create" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <Plus className="w-6 h-6 text-blue-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Create RFQ</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Post new opportunity</p>
                  </div>
                </Link>
                <Link to="/gov/proposals" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <Users className="w-6 h-6 text-green-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Review Proposals</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Review vendor proposals</p>
                  </div>
                </Link>
                <Link to="/gov/vendors" className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <BarChart3 className="w-6 h-6 text-purple-600 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-gray-100">Certified Vendors</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Browse vendor directory</p>
                  </div>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Quote Dashboard */}
        <GovQuoteDashboard
          userRole={userRole}
          quotes={quotes}
          loading={loading}
          onQuoteAction={handleQuoteAction}
        />

        {/* RFQ Modal */}
        <GovRfqModal
          isOpen={isRfqModalOpen}
          onClose={() => setIsRfqModalOpen(false)}
          onSuccess={handleRfqSuccess}
          userRole={userRole}
        />
      </div>
    </div>
  )
}